(() => {
  if (customElements.get('shopal-canyon')) return;

  const clamp = v => Math.max(0, Math.min(1, v));
  const ease = v => {
    v = clamp(v);
    return v * v * (3 - 2 * v);
  };

  const ARRIVEE = 1400;
  const MONTEE = 1200;

  class ShopalCanyon extends HTMLElement {
    connectedCallback() {
      this.abort = new AbortController();
      const signal = this.abort.signal;

      this.host = this.closest('.shopal-canyon-section') || this.parentElement;
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.track = this.querySelector('.sc__track');
      this.loader = this.querySelector('.sc__loader');

      this.frame = 0;
      this.pageFrame = 0;
      this.current = 0;
      this.target = 0;
      this.animation = null;
      this.ready = false;
      this.stage = 'initial';
      this.wheelLast = -Infinity;
      this.wheelUsed = false;
      this.wheelSum = 0;

      this.tick = this.tick.bind(this);

      this.request = () => {
        if (
          this.ready &&
          !this.frame &&
          !this.motion.matches &&
          !document.hidden
        ) {
          this.frame = requestAnimationFrame(this.tick);
        }
      };

      this.observe = () => {
        const r = this.host.getBoundingClientRect();

        this.active =
          r.bottom > 0 &&
          r.top < innerHeight &&
          !document.hidden;

        this.classList.toggle('is-visible', this.active);
        this.target = r.top < -8 ? 1 : 0;

        if (this.stage !== 'rising') {
          if (r.top >= -4) {
            this.stage = 'initial';
          } else if (this.gallery?.getBoundingClientRect().top <= 1) {
            this.stage = 'done';
          } else if (this.current === 1) {
            this.stage = 'ready';
          } else {
            this.stage = 'arriving';
          }
        }

        this.request();
      };

      this.reset = () => {
        this.cancelRise();
        cancelAnimationFrame(this.frame);
        this.frame = 0;
        this.animation = null;

        this.host.classList.toggle(
          'has-scroll-intro',
          !this.motion.matches
        );

        this.classList.toggle('is-running', !this.motion.matches);
        this.observe();
        this.current = this.motion.matches ? 1 : this.target;
        this.paint(this.current);
      };

      addEventListener('scroll', this.observe, {
        passive: true,
        signal
      });

      addEventListener('resize', this.observe, {
        passive: true,
        signal
      });

      document.addEventListener('visibilitychange', this.observe, {
        signal
      });

      this.motion.addEventListener('change', this.reset, { signal });

      this.reset();

      const decoded = Promise.allSettled(
        [...this.querySelectorAll('img')].map(im =>
          im.decode ? im.decode() : Promise.resolve()
        )
      );

      const timeout = new Promise(resolve => {
        this.timer = setTimeout(resolve, 4000);
      });

      Promise.race([decoded, timeout]).then(() => {
        clearTimeout(this.timer);
        if (signal.aborted) return;

        this.ready = true;
        this.observe();

        if (this.motion.matches) this.paint(1);
      });

      customElements.whenDefined('shopal-categories').then(() => {
        if (signal.aborted) return;

        const next = this.host.nextElementSibling;

        if (!next?.classList.contains('shopal-categories-section')) {
          return;
        }

        this.gallery = next.querySelector('shopal-categories.sct');
        if (!this.gallery) return;

        this.host.classList.add('shopal-soft-intro');
        this.installGestures(signal);
        this.observe();
      });
    }

    paint(t) {
      const phase = ease((t - .35) / .45);
      const travel = ease(t / .90);

      const values = {
        '--sc-rock-zoom': 1.04 + .045 * Math.sin(Math.PI * t),
        '--sc-phase': phase,
        '--sc-enter': 1,
        '--sc-load': clamp(t / .35),
        '--sc-zoom': .40 + travel * .60,
        '--sc-rise': `${(1 - travel) * 65}px`,
        '--sc-angle': `${(1 - travel) * -28}deg`,
        '--sc-pan': `${Math.sin(t * Math.PI * 2) * 18 * (1 - travel)}deg`,
        '--sc-scroll': ease(t),
        '--shopal-ray-angle': `${-18 + 36 * t}deg`,
        '--shopal-ray-shift': `${-20 + 40 * t}px`,
        '--shopal-sun-shift': `${-7 + 14 * t}vw`
      };

      for (const [key, value] of Object.entries(values)) {
        this.style.setProperty(key, String(value));
      }

      this.track?.setAttribute(
        'aria-valuenow',
        String(Math.round(clamp(t / .35) * 100))
      );

      this.loader?.setAttribute('aria-hidden', String(phase > .95));
    }

    tick(now) {
      this.frame = 0;

      if (this.motion.matches || document.hidden) return;

      if (!this.animation || this.animation.to !== this.target) {
        this.animation = {
          from: this.current,
          to: this.target,
          start: now
        };
      }

      const a = this.animation;
      const p = this.active ? clamp((now - a.start) / ARRIVEE) : 1;

      this.current = p === 1
        ? a.to
        : a.from + (a.to - a.from) * ease(p);

      this.paint(this.current);

      if (this.current !== this.target) {
        this.request();
      } else {
        this.animation = null;

        if (this.stage === 'arriving' && this.current === 1) {
          this.stage = 'ready';
        }
      }
    }

    canControl(target) {
      if (
        this.motion.matches ||
        !this.gallery?.classList.contains('is-scroll')
      ) {
        return false;
      }

      if (
        target?.closest?.(
          'input,textarea,select,[contenteditable="true"],dialog,[role="dialog"],details[open]'
        )
      ) {
        return false;
      }

      const modalOpen = [
        ...document.querySelectorAll('dialog[open],[aria-modal="true"]')
      ].some(el =>
        el.getClientRects().length &&
        getComputedStyle(el).visibility !== 'hidden'
      );

      if (modalOpen) return false;
      if (getComputedStyle(document.body).overflowY === 'hidden') {
        return false;
      }

      const r = this.host.getBoundingClientRect();
      return r.top <= 4 && r.bottom > 0;
    }

    advance() {
      if (this.stage === 'initial') {
        this.stage = 'arriving';
        this.animation = null;

        scrollTo({
          top: scrollY + this.host.getBoundingClientRect().top + 12,
          behavior: 'instant'
        });

        this.observe();
      } else if (this.stage === 'ready') {
        this.rise();
      }
    }

    rise() {
      this.stage = 'rising';

      const from = scrollY;
      let start;

      const step = now => {
        if (!this.isConnected || this.motion.matches) {
          this.cancelRise();
          return;
        }

        start ??= now;

        const p = clamp((now - start) / MONTEE);
        const to = scrollY + this.gallery.getBoundingClientRect().top;

        scrollTo({
          top: from + (to - from) * ease(p),
          behavior: 'instant'
        });

        if (p < 1) {
          this.pageFrame = requestAnimationFrame(step);
        } else {
          this.pageFrame = 0;
          this.stage = 'done';
        }
      };

      this.pageFrame = requestAnimationFrame(step);
    }

    cancelRise() {
      cancelAnimationFrame(this.pageFrame);
      this.pageFrame = 0;

      if (this.stage === 'rising') this.stage = 'ready';
    }

    installGestures(signal) {
      const stop = e => {
        if (e.cancelable) e.preventDefault();
        e.stopPropagation();
      };

      addEventListener('wheel', e => {
        if (
          e.ctrlKey ||
          Math.abs(e.deltaX) > Math.abs(e.deltaY) ||
          !e.deltaY
        ) {
          return;
        }

        if (e.deltaY < 0) {
          this.cancelRise();
          this.wheelUsed = false;
          return;
        }

        const now = performance.now();

        if (now - this.wheelLast > 240) {
          this.wheelUsed = false;
          this.wheelSum = 0;
        }

        this.wheelLast = now;

        if (!this.canControl(e.target)) return;
        if (this.stage === 'done' && !this.wheelUsed) return;

        stop(e);

        if (this.wheelUsed) return;

        this.wheelSum += e.deltaY * (
          e.deltaMode === 1 ? 16 :
          e.deltaMode === 2 ? innerHeight : 1
        );

        if (this.wheelSum >= 10) {
          this.wheelUsed = true;
          this.advance();
        }
      }, {
        capture: true,
        passive: false,
        signal
      });

      addEventListener('touchstart', e => {
        this.touch =
          e.touches.length === 1 &&
          this.stage !== 'done' &&
          this.canControl(e.target)
            ? {
                x: e.touches[0].clientX,
                y: e.touches[0].clientY,
                used: false,
                axis: null
              }
            : null;
      }, {
        capture: true,
        passive: true,
        signal
      });

      addEventListener('touchmove', e => {
        const t = this.touch;

        if (!t || e.touches.length !== 1) return;

        const dy = t.y - e.touches[0].clientY;
        const dx = t.x - e.touches[0].clientX;

        if (t.used) {
          stop(e);
          return;
        }

        if (!t.axis && Math.max(Math.abs(dx), Math.abs(dy)) > 5) {
          t.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        }

        if (t.axis !== 'y') return;

        if (dy < 0) {
          this.cancelRise();
          this.touch = null;
          return;
        }

        if (!this.canControl(e.target)) return;

        stop(e);

        if (dy >= 20) {
          t.used = true;
          this.advance();
        }
      }, {
        capture: true,
        passive: false,
        signal
      });

      const endTouch = () => {
        this.touch = null;
      };

      addEventListener('touchend', endTouch, {
        capture: true,
        passive: true,
        signal
      });

      addEventListener('touchcancel', endTouch, {
        capture: true,
        passive: true,
        signal
      });

      addEventListener('keydown', e => {
        if (['ArrowUp', 'PageUp', 'Home', 'Escape'].includes(e.key)) {
          this.cancelRise();
          return;
        }

        if (
          !['ArrowDown', 'PageDown', ' '].includes(e.key) ||
          e.altKey || e.ctrlKey || e.metaKey || e.shiftKey
        ) {
          return;
        }

        if (
          e.target.closest?.('a,button,summary,[role="button"]') ||
          !this.canControl(e.target)
        ) {
          return;
        }

        if (this.stage === 'done') return;

        stop(e);

        if (!e.repeat) this.advance();
      }, {
        capture: true,
        signal
      });
    }

    disconnectedCallback() {
      this.abort?.abort();
      clearTimeout(this.timer);
      cancelAnimationFrame(this.frame);
      this.cancelRise();

      this.host?.classList.remove(
        'has-scroll-intro',
        'shopal-soft-intro'
      );
    }
  }

  customElements.define('shopal-canyon', ShopalCanyon);
})();